import {
  GraphNodeType,
  getAttributeIconAsComponentType,
  getContainedBy,
  getGraphNodeTypeColor,
  getIndexForNode,
} from "@/common";
import GraphView, { type GraphViewNodeProps } from "@/components/GraphView/GraphView";

import type { ControlType } from "@imminently/interview-sdk";
import { Tooltip } from "@mui/material";
import type { ParsedRuleGraph } from "@packages/commons";
import cn from "clsx";
import find from "lodash/find";
import React from "react";
import { ControlIcons } from "../../../pages/models/release/Interview/Designer/components/ControlIcon";
import NodeValue from "../../GraphVisualisation/components/AttributeInfo/NodeValue";
import { useGraphVisualisation } from "../../GraphVisualisation/hooks/useGraphVisualisation";
import GraphNodeBar from "./GraphNodeBar";
import GraphNodeContextMenu from "./GraphNodeContextMenu";
import GraphNodeCountEar from "./GraphNodeCountEar";

import { hexToHSL } from "@/theme";
import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import CoreGraphNode from "@imminently/decisively-ui/GraphNode";
import stl from "./DebuggerGraphNode.module.scss";
import Icon from "@imminently/decisively-ui/Icon";

export const defaultTypeFunc = (node: any, graph: any) => {
  // intermediate is the safest default, as you'll have both types of connector, and won't miss out on drawing an edge
  if (!graph) return "intermediate";

  if ((graph.predecessors(node.id) || []).length === 0) {
    return "goal";
  }
  if ((graph.successors(node.id) || []).length > 0) {
    return "intermediate";
  }
  if (node.conditions || node.value || node.rows) {
    // These are nodes that have a default value (as source nodes have no calculations on them)
    return "intermediate";
  }

  // const edges = find(graph.edges(), { target: node.id });
  // console.log("++++++++++++++++++ node", node);
  // console.log("++++++++++++++++++ edges", edges);

  return "source";
};

const nodeTypeToLabel = (type: string) => {
  switch (type) {
    case "goal":
      return "Goal";
    case "intermediate":
      return "Derived";
    default:
      return "Input";
  }
};

export const byValueTypeFunc = () => "byValue";

export interface GraphNodeProps extends GraphViewNodeProps {
  classes?: any;
  graph?: ParsedRuleGraph;
  horizontal?: boolean;
}

const GraphNode = (props: GraphNodeProps) => {
  const {
    id,
    data,
    includeContextMenu = true,
    xPos,
    yPos,
    isDragging,
    isDecisionFlow,
    graph: propsGraph,
    horizontal,
  } = props;
  const node = data.node;
  const nodeId = node?.id;
  const nodePath = node?.path;
  const nodeType = (node?.type || "") as ControlType;
  const {
    colorScheme,
    entityColorMapping,
    graph: graphVizGraph,
    setRestrictedViewNodesToMerge,
    debug,
    restrictedView,
  } = useGraphVisualisation();
  const graph = propsGraph || graphVizGraph;

  // const searchMatchId = useSearchMatchCtx();
  // const className = React.useMemo(
  //   () => (searchMatchId === nodeId ? SelectedSearchNodeClsnm : undefined),
  //   [searchMatchId, nodeId],
  // );
  const indexInfo: any = getIndexForNode(node, graph) || {};

  let nodeIndex = "";
  if (indexInfo) {
    nodeIndex = indexInfo.hint ?? indexInfo.value;
  }
  const entityName = node?.entity || "";
  const isGlobalEntity = entityName === "global";

  const graphNodeType = defaultTypeFunc(node, graph);

  let nodeClass: string = graphNodeType;

  let hasConnectorTop = graphNodeType === "intermediate" || graphNodeType === "source";
  let hasConnectorBottom = isDecisionFlow ? true : graphNodeType === "intermediate" || graphNodeType === "goal"; // TODO ds refine this, we might not always need the connector but we need to see the flow graph
  const isValueNode = debug;
  const isTargetNode = (() => {
    if (restrictedView?.rootPath) {
      if (nodePath) {
        return nodePath === restrictedView?.rootPath;
      }
      return nodeId === restrictedView?.rootPath;
    }

    return false;
  })();

  // -- value (debugger) node specific
  const edgesStore = GraphView.useStore((state) => state.edges);
  if (isValueNode) {
    const edges = edgesStore;
    const parents = find(edges, { target: id });
    const children = find(edges, { source: id });
    const value = typeof node.input === "undefined" ? node.derived : node.input;

    hasConnectorTop = !!parents;
    hasConnectorBottom = !!children;

    if (colorScheme === "by-value") {
      // we twiddle the nodeClass here slightly too in order to get the right color
      if (value === false) {
        nodeClass = "falsy";
      } else if (value === null) {
        nodeClass = "unknown";
      } else if (typeof value === "undefined") {
        nodeClass = "uncertain";
      } else {
        nodeClass = "truthy";
      }
    }
    // TODO I'm pretty sure this color scheme makes no sense on a debug graph, so let's remove it if everyone agrees
    // else if (colorScheme === "by-type") {
    //   // we won't know the "type" as the nodeClass will have been set as "byValue" here, so we need to have a reasonable guess
    //   if (hasConnectorTop && hasConnectorBottom) {
    //     nodeClass = "intermediate";
    //   } else if (hasConnectorBottom) {
    //     nodeClass = "goal";
    //   } else {
    //     nodeClass = "source";
    //   }
    // }
  }

  /** the by-entity color scheme trumps everything */
  if (colorScheme === "by-entity") {
    const entityColor = entityColorMapping.find((mapping) => mapping.entity === entityName);
    if (entityColor) {
      nodeClass = entityColor.colorKey.replace("--value-color-", "");
    }
  }

  // -- rendering

  const entityFlag = () => {
    const cleanIndex = nodeIndex && nodeIndex.length > 8 ? `${nodeIndex.substring(0, 8)}...` : nodeIndex;
    const contained = getContainedBy(node.path, graph);
    const idLabel = typeof indexInfo.hint !== "undefined" ? indexInfo.hint : nodeIndex;

    const content = (
      <CoreGraphNode.Flag className={stl.entity_flag}>
        {nodeTypeToLabel(graphNodeType)} | {entityName}
        {cleanIndex ? `/${cleanIndex}` : ""}
      </CoreGraphNode.Flag>
    );

    if (isGlobalEntity) {
      return content;
    }

    return (
      <Tooltip
        title={
          <div style={{ whiteSpace: "pre-line" }}>
            <strong>Entity</strong>: {entityName}
            {"\n"}
            {idLabel ? (
              <>
                <strong>Id</strong>: {idLabel}
              </>
            ) : null}
            {indexInfo.hint ? (
              <>
                {"\n"}
                <span>
                  <strong>Identifier</strong>: {indexInfo.value}
                </span>
              </>
            ) : null}
            {contained ? (
              <>
                {"\n"}
                <strong>Contained by</strong>
                <span>{contained.replace(/^Contained by/, "")}</span>
              </>
            ) : null}
          </div>
        }
      >
        {content}
      </Tooltip>
    );
  };

  const renderHandle = (type: "target" | "source", position: any) => {
    return (
      <GraphView.Handle
        type={type}
        position={position}
        className={stl.connector_handle}
        draggable={false}
        isConnectable={false}
        // id={id || nodePath || nodeId}
      />
    );
  };

  const renderTypeIcon = () => {
    // TODO check if we can update this one to use the new icons?
    const IconComponent_ = (ControlIcons as any)[nodeType] || ControlIcons.unassigned!;
    const IconComponent = getAttributeIconAsComponentType((nodeType || "") as any, IconComponent_);

    // if (ControlIcons.unassigned === IconComponent_) {
    //   console.warn(`Unassigned icon for node type: ${nodeType}`);
    // }

    return (
      <CoreGraphNode.TypeIcon className={cn(stl.type_icon, stl[nodeClass])}>
        <IconComponent />
      </CoreGraphNode.TypeIcon>
    );
  };

  const getNodeLabel = () => {
    const maxLabelLength = 90;
    const label = node.description || "";
    if (label.length <= maxLabelLength) {
      return label;
    }
    const labelShort = `${label.substr(0, maxLabelLength)}...`;
    // now trim the label to the last word
    const labelTrimmed = `${labelShort.slice(0, labelShort.lastIndexOf(" "))}...`;

    return labelTrimmed;
  };

  const renderValueSection = () => {
    return (
      <div className={cn(stl.value_section, stl[nodeClass])}>
        <AttributeValue
          node={node}
          showType={false}
          temporalTextProps={{
            maxEntries: 2,
          }}
        />
      </div>
    );
  };

  const renderTargetIcon = () => {
    if (!isTargetNode) {
      return null;
    }

    return (
      <div className={stl.target_icon}>
        <Icon name="target" />
      </div>
    );
  };

  const getNamespaceLabel = () => {
    const namespace = node.definedIn?.replace(".docx", "").replace(".xlsx", "");
    return namespace ? <div className={stl.namespace}>{namespace}</div> : null;
  };

  return (
    <CoreGraphNode
      className={cn(stl.container, stl[nodeClass], { [stl.target_node]: isTargetNode }, { [stl.dragging]: isDragging })}
    >
      {hasConnectorTop ? renderHandle("target", horizontal ? GraphView.Position.Left : GraphView.Position.Top) : null}
      {entityFlag()}
      {renderTargetIcon()}
      {!horizontal ? (
        <GraphNodeCountEar
          parsedGraph={graph}
          currNodeId={nodePath || nodeId}
          visibleNodeIds={restrictedView?.visibleNodes || null}
          isTargetNode={isTargetNode}
          isDragging={isDragging}
          addVisibleNodes={setRestrictedViewNodesToMerge}
          showHidden={!(debug ?? false)}
        />
      ) : null}
      <CoreGraphNode.Header className={cn(stl.col_container, stl[nodeClass])}>
        <CoreGraphNode.ColLhs>{renderTypeIcon()}</CoreGraphNode.ColLhs>
        <CoreGraphNode.ColRhs className={cn(stl.col_rhs, { [stl.value_node]: isValueNode })}>
          {/*getNamespaceLabel()*/}
          {getNodeLabel()}
          {/* the entity name is already above, so why repeat it? */}
          {/* {isValueNode ? <div><small>{entityNameWithIndex}</small></div> : null} */}
          {/* {isValueNode && nodeIndex ? <div><small>{nodeIndex}</small></div> : null} */}
          {includeContextMenu ? (
            <GraphNodeContextMenu
              node={node}
              // okay, so these assumptions don't work...
              // isGoal={graphNodeType === "goal" || (graphNodeType === "byValue" && hasConnectorBottom && !hasConnectorTop)}
              isGoal={true}
              isTargetNode={isTargetNode}
            />
          ) : null}
        </CoreGraphNode.ColRhs>
      </CoreGraphNode.Header>
      <CoreGraphNode.Content>
        {isValueNode ? renderValueSection() : null}
        <GraphNodeBar node={node} />
      </CoreGraphNode.Content>
      {hasConnectorBottom
        ? renderHandle("source", horizontal ? GraphView.Position.Right : GraphView.Position.Bottom)
        : null}
    </CoreGraphNode>
  );
};

export default GraphNode;
